/**
 * calibrationDerivation
 *
 * Framework-agnostic derivation of the three calculated calibration values
 * from raw inputs (delivery rate, tractor speed, orchard tree spacing,
 * orchard tree density). Extracted from `AddCalibrationModal` so the formula
 * can be unit-tested without React and reused on the server if needed.
 *
 * The formulas are intentionally identical to the previous in-modal
 * implementation:
 *
 *   treesPerMinute   = metersPerMinute / treeSpacing
 *   volumePerTree    = deliveryPerMinute / treesPerMinute
 *   litresPerHectare = volumePerTree * treesPerHectare
 *
 * Any missing input collapses the affected output to `undefined` rather than
 * throwing. Division by zero (a `treeSpacing` of 0) is treated as a missing
 * input and also yields `undefined`.
 */

export interface DerivationInputs {
  deliveryPerMinute?: number;
  metersPerMinute?: number;
  treeSpacing?: number;
  treesPerHectare?: number;
}

export interface DerivedValues {
  treesPerMinute?: number;
  volumePerTree?: number;
  litresPerHectare?: number;
}

export function deriveValues(inputs: DerivationInputs): DerivedValues {
  const { deliveryPerMinute, metersPerMinute, treeSpacing, treesPerHectare } = inputs;

  const treesPerMinute =
    metersPerMinute !== undefined && treeSpacing !== undefined && treeSpacing > 0
      ? metersPerMinute / treeSpacing
      : undefined;

  const volumePerTree =
    deliveryPerMinute !== undefined && treesPerMinute !== undefined && treesPerMinute > 0
      ? deliveryPerMinute / treesPerMinute
      : undefined;

  const litresPerHectare =
    volumePerTree !== undefined && treesPerHectare !== undefined
      ? volumePerTree * treesPerHectare
      : undefined;

  return { treesPerMinute, volumePerTree, litresPerHectare };
}

/**
 * Resolve the "effective" calibration value that downstream consumers see.
 *
 * The dual-value schema stores both the formula-derived value (frozen at save
 * time) and an optional operator override. The effective value is the
 * override when one is supplied (and is a positive number) and the derived
 * value otherwise. An override of 0 is treated as absent — zero/negative
 * overrides are rejected by form validation, but the resolver is defensive
 * so a stray 0 cannot leak through as the effective value.
 */
export function resolveEffective(args: {
  derived?: number;
  override?: number;
}): number | undefined {
  const { derived, override } = args;
  if (override !== undefined && override > 0) return override;
  return derived;
}

